import { useFetcher } from '@remix-run/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from '@remix-run/react';
import type { RemoveBuildHubMutation } from 'generated/graphql';
import { REMOVE_BUILD_HUB_API_ROUTE } from '~/routes/remix-api.app.build-hub-remove';

interface UseBuildHubDeleteReturn {
    deleteBuildHub: (id: string) => void;
    isDeleting: boolean;
    error: string | null;
    isSuccess: boolean;
    isIdle: boolean;
}

interface UseBuildHubDeleteOptions {
    shouldNavigateOnSuccess?: boolean;
}

/**
 * Hook for deleting build hubs with comprehensive error handling and automatic navigation
 *
 * Automatically redirects users away from deleted build hub pages to prevent 404 errors.
 *
 * @returns Object containing delete function and state information
 * @example
 * const { deleteBuildHub, isDeleting, error, isSuccess } = useBuildHubDelete();
 *
 * // Usage
 * try {
 *   await deleteBuildHub('build-hub-id');
 *   // Hook automatically handles navigation if on deleted build hub page
 * } catch (error) {
 *   // Handle error
 * }
 */
export const useBuildHubDelete = (
    options: UseBuildHubDeleteOptions = {}
): UseBuildHubDeleteReturn => {
    const { shouldNavigateOnSuccess = false } = options;
    const deleteFetcher = useFetcher<RemoveBuildHubMutation>();
    const navigate = useNavigate();

    const deletedBuildHubIdRef = useRef<string | null>(null);

    const data = deleteFetcher.data?.buildHubDelete;
    const isDeleting = deleteFetcher.state !== 'idle';
    const isIdle = !isDeleting;
    const isSuccess = Boolean(isIdle && data && data.__typename === 'BuildHub');

    const deleteBuildHub = useCallback(
        (id: string) => {
            // Store the ID so we can use it for navigation after deletion
            deletedBuildHubIdRef.current = id;
            deleteFetcher.submit(
                { id },
                {
                    method: 'post',
                    action: REMOVE_BUILD_HUB_API_ROUTE,
                }
            );
            if (shouldNavigateOnSuccess) {
                navigate('/app/my-projects', { replace: true });
            }
        },
        [deleteFetcher, shouldNavigateOnSuccess, navigate]
    );

    // Handle automatic navigation after successful deletion
    useEffect(() => {
        const fetcherCompleted = deleteFetcher.state === 'idle' && data;
        const isSuccess = fetcherCompleted && data.__typename === 'BuildHub';

        if (isSuccess) {
            const deletedBuildHubId = deletedBuildHubIdRef.current;
            if (deletedBuildHubId) {
                deletedBuildHubIdRef.current = null;
            }
        }
    }, [deleteFetcher.state, data]);

    const error = useMemo(() => {
        if (isIdle && data && data.__typename !== 'BuildHub') {
            return 'Message' in data
                ? data.Message
                : 'Failed to remove build hub';
        }
        return null;
    }, [isIdle, data]);

    return {
        deleteBuildHub,
        isDeleting,
        error,
        isSuccess,
        isIdle,
    };
};
